import { subscribeNavigation } from '@go-beyond/react/browser'
import { useEffect, useRef, useState } from 'react'

type ProgressPhase = 'idle' | 'loading' | 'finishing'

const MIN_VISIBLE_MS = 280
const FADE_MS = 220

/**
 * Thin top-of-viewport progress bar driven by GoBeyond soft-navigation
 * lifecycle events. Mount once under the persistent layout.
 */
export function NavigationProgress() {
  const [phase, setPhase] = useState<ProgressPhase>('idle')
  const startedAt = useRef(0)
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return subscribeNavigation((event) => {
      if (finishTimer.current) {
        clearTimeout(finishTimer.current)
        finishTimer.current = null
      }
      if (event.type === 'start') {
        startedAt.current = Date.now()
        setPhase('loading')
        return
      }
      if (event.type === 'success' || event.type === 'error') {
        const elapsed = Date.now() - startedAt.current
        const wait = Math.max(0, MIN_VISIBLE_MS - elapsed)
        finishTimer.current = setTimeout(() => {
          setPhase('finishing')
          finishTimer.current = setTimeout(() => {
            setPhase('idle')
            finishTimer.current = null
          }, FADE_MS)
        }, wait)
      }
    })
  }, [])

  useEffect(() => {
    return () => {
      if (finishTimer.current) clearTimeout(finishTimer.current)
    }
  }, [])

  if (phase === 'idle') return null

  const width = phase === 'loading' ? '72%' : '100%'
  const opacity = phase === 'finishing' ? 0 : 1

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 overflow-hidden"
      data-navigation-progress={phase}
      role="progressbar"
      aria-hidden="true"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={phase === 'finishing' ? 100 : 70}
    >
      <div
        className="h-full origin-left bg-[var(--accent)] shadow-[0_0_8px_color-mix(in_oklab,var(--accent)_55%,transparent)] transition-[width,opacity] ease-out"
        style={{
          width,
          opacity,
          transitionDuration: phase === 'loading' ? '8s' : `${FADE_MS}ms`,
          transitionTimingFunction:
            phase === 'loading' ? 'cubic-bezier(0.1, 0.05, 0, 1)' : 'ease-out',
        }}
      />
    </div>
  )
}
